import { Box, Flex } from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { LoadingIndicator, Spinner, useToast } from "../../../components";
import { formatDetails } from "../../../utils/call";
import { clipDefaultEndTime } from "../../../utils/clip";
import { copy } from "../../../utils/clipboard";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import { formatSegment } from "../../../utils/transcript";
import { usePushCandidateNotes } from "../../browser-extension/hooks/usePushCandidateNotes";
import {
  CallAiSummaryFormat,
  CallStatus,
  useCallAiSummaryLazyQuery,
  useCallQuery,
  useUpdateCallAiSummaryFormatMutation,
  useViewCallMutation,
} from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import { useIsExtension } from "../../hooks/useAppEnvironmentContext";
import useCurrentUser from "../../hooks/useCurrentUser";
import useMouseHasMoved from "../../hooks/useMouseHasMoved";
import { ClipRange, VideoDisplayMode } from "../../types";
import CreateClipModal from "../CreateClipModal/CreateClipModal";
import useMediaPlayer from "../MediaPlayer/useMediaPlayer";
import useVideoState from "../MediaPlayer/useVideoState";
import { RecordingLayout, Transcript } from "../Recording";
import { buildProgressMessage } from "../Recording/Transcript/utils";
import useVisitorAnalytics from "../Recording/useVisitorAnalytics";
import CallErrorState from "./CallErrorState";
import CallSidebar from "./CallSidebar";
import CallVideo from "./CallVideo";
import InterviewDetailsHeader from "./InterviewDetailsHeader";
import useCallSpeakerOptions from "./useCallSpeakerOptions";
import useCallTranscript from "./useCallTranscript";
import useStartTime from "./useStartTime";
import { CallTab, isCallTab } from "./utils";

export type InterviewDetailsProps = {
  callId: string;
  disableAutofill?: boolean;
  notesForCurrentUserOnly?: boolean;
};

const InterviewDetails: React.FC<InterviewDetailsProps> = ({
  callId,
  disableAutofill,
  notesForCurrentUserOnly = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // DO NOT REMOVE
  // We use this for sales demos.
  const redactedRecording = searchParams.has("redacted");
  const tabFromUrl = searchParams.get("tab");
  const defaultTab =
    tabFromUrl && isCallTab(tabFromUrl) ? tabFromUrl : CallTab.NOTES;
  const toast = useToast();

  const currentUser = useCurrentUser();
  const { recordingAllowed } = currentUser.organization;
  const isExtension = useIsExtension();
  const sendGAEvent = useSendGAEvent();

  // Signal the Workday content script that this interview is selected
  usePushCandidateNotes({
    callIds: [callId],
    selectedCallId: callId,
  });

  // Query for the call at hand
  const { data, loading, error, stopPolling } = useCallQuery({
    variables: {
      id: callId,
      // As above, DO NOT REMOVE
      redactedRecording,
    },
    // pollInterval: 5000,
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  /**
   * The call that forms the basis for this interview to display
   */
  const call = data?.call;

  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const transcriptQuery = useCallTranscript({
    callId,
    notesForCurrentUserOnly,
  });

  const { speakerOptions } = useCallSpeakerOptions({ callId });

  const [clipRange, setClipRange] = useState<ClipRange | null>(null);
  // Set up the media player & video
  const [mediaPlayerRef, listeners, player] = useMediaPlayer({
    enabled: !clipRange,
  });

  // Set up audio/video player controlls
  const { seek, time, play, canPlay, duration } = player;
  const seekAndPlay = useCallback(
    (t: number): void => {
      seek(t);
      if (!autoScrollEnabled) {
        setAutoScrollEnabled(true);
      }
      play();
    },
    [seek, autoScrollEnabled]
  );

  useEffect(() => {
    if (clipRange) {
      player.pause();
    }
  }, [clipRange]);

  const startTime = useStartTime(duration);

  useEffect(() => {
    if (canPlay) {
      seek(startTime);
    }
  }, [canPlay, startTime, seek]);

  const onCreateClipClick = recordingAllowed
    ? (range?: ClipRange): void => {
        setClipRange({
          start: range?.start ?? time,
          end: range?.end ?? clipDefaultEndTime(call?.duration, time),
        });
      }
    : undefined;

  const [currentTab, setCurrentTab] = useState(defaultTab);
  useEffect(() => {
    searchParams.set("tab", currentTab);
    setSearchParams(searchParams.toString(), { replace: true });
  }, [currentTab]);

  let videoDisplayMode = VideoDisplayMode.NO_RECORDING;
  if (call?.streamableAudio && !call?.streamableVideo) {
    videoDisplayMode = VideoDisplayMode.AUDIO;
  } else if (call?.streamableVideo) {
    videoDisplayMode = VideoDisplayMode.VIDEO;
  } else if (call?.isProcessing) {
    videoDisplayMode = VideoDisplayMode.PROCESSING;
  } else if (call?.status === CallStatus.InProgress) {
    videoDisplayMode = VideoDisplayMode.IN_PROGRESS;
  }

  const {
    containerRef,
    videoCallbackRef,
    fullScreenControls,
    pipControls,
    isVideoVisible,
    hideVideo,
    focusVideo,
  } = useVideoState(videoDisplayMode);

  /**
   * The audio or video content URL to stream from
   */
  const url = call?.streamableVideo
    ? call?.streamableVideo?.url
    : call?.streamableAudio?.url;
  const [mediaSrc, setMediaSrc] = useState(url);
  if (mediaSrc?.split("?")[0] !== url?.split("?")[0]) {
    setMediaSrc(url);
  }

  // Log views to audit log & activity feed
  const [viewCall, { called: callViewLogged }] = useViewCallMutation();
  const mouseHasMoved = useMouseHasMoved();
  useEffect(() => {
    if (call && mouseHasMoved && !callViewLogged) {
      viewCall({ variables: { callId } });
    }
  }, [call, mouseHasMoved, callViewLogged]);

  // Log the view to segment
  useVisitorAnalytics({
    canLogCallView: callViewLogged,
    application: isExtension ? "extension" : "app",
    callId: call?.id,
    transcriptionStatus: call?.transcriptionStatus as string,
    interviewerIds: call?.interviewerIds,
  });

  const onCopyTranscript = useCallback((): void => {
    if (!call) {
      return;
    }
    sendGAEvent("copy_transcript", "call_review");
    const callDetails = formatDetails(call);
    const speakerLabels = call.speakers.map((speaker) => speaker.label);
    const transcriptText =
      transcriptQuery.data.transcript
        ?.map((segment) => formatSegment(speakerLabels, segment))
        .reduce((previous, current) => `${previous}\n\n${current}`, "") ?? "";
    const text = `${callDetails}\n\n${transcriptText}`;
    copy(text);
    toast({
      title: "Transcript copied to clipboard",
      status: "success",
    });
  }, [call, transcriptQuery, toast]);

  const [fetchSummary] = useCallAiSummaryLazyQuery({});
  const [updateSummaryFormat] = useUpdateCallAiSummaryFormatMutation({});
  const writeupEnabled = useFeatureFlag("ai-summary:writeup");
  // Used to make the AI Summary experience feel more seamless. It will generate
  // a missing summary, so by the time the user gets to the tab, it should be
  // almost finished generating
  useEffect(() => {
    if (writeupEnabled && mouseHasMoved) {
      fetchSummary({
        variables: {
          callId,
          format: CallAiSummaryFormat.Writeup,
        },
        fetchPolicy: "network-only",
        onCompleted: (res) => {
          if (!res.callAiSummary?.id) {
            updateSummaryFormat({
              variables: {
                callId,
                format: CallAiSummaryFormat.Writeup,
                targetSpeakerTags: [],
                customTopics: [],
              },
            });
          }
        },
      });
    }
  }, []);

  // If the call is loading or we're still figuring out if it was deleted
  // or never existed etc, show loading indicator
  if (loading || transcriptQuery.data.loading) {
    return <LoadingIndicator pt="10vh" />;
  }

  if (error || !call) {
    // This can only happen if the query succeeded initially but an error occurs
    // when subsequently polling the same query.
    stopPolling();

    return (
      <Flex flexDirection="column" overflowY="hidden">
        <CallErrorState view="error" />
      </Flex>
    );
  }

  let transcriptSection = null;
  if (call.isProcessing || call.status === CallStatus.InProgress) {
    let recordingMessage;
    let hasRecording = false;
    if (call.status !== CallStatus.InProgress) {
      hasRecording = Boolean(call.streamableAudio || call.streamableVideo);
      recordingMessage = hasRecording
        ? "Transcribing recording..."
        : "Processing and transcribing recording…";
    } else {
      recordingMessage = "Interview is in progress...";
    }
    transcriptSection = (
      <Flex flexDirection="column" align="center" py="10">
        <Spinner width="48px" height="48px" />
        <Box
          fontWeight="bold"
          color="gray.800"
          fontSize="lg"
          textAlign="center"
          marginTop={3}
        >
          {recordingMessage}
          {call.duration && (
            <Box fontSize="md" fontWeight="400" mt={2} px={6}>
              {buildProgressMessage(call.endTime)}
            </Box>
          )}
          {hasRecording && (
            <Box width="100%" fontSize="lg" my={6} textAlign="center">
              {`Click "Play" to start ${
                call.streamableVideo ? "watching" : "listening"
              } while we finish transcribing the interview.`}
            </Box>
          )}
          {call.status === CallStatus.InProgress && (
            <Box fontSize="md" fontWeight="400" mt={2} px={6}>
              When the interview is completed and processed, you can play back
              the recording and review the transcript.
            </Box>
          )}
        </Box>
      </Flex>
    );
  } else {
    transcriptSection = (
      <Transcript
        h="100%"
        callId={call.id}
        speakers={call.speakers}
        player={player}
        autoScrollEnabled={autoScrollEnabled}
        setAutoScrollEnabled={setAutoScrollEnabled}
        onCreateClipClick={onCreateClipClick}
        onCopyTranscript={onCopyTranscript}
        isVideoCall={videoDisplayMode === VideoDisplayMode.VIDEO}
        isVideoVisible={isVideoVisible}
        hideVideo={hideVideo}
        focusVideo={focusVideo}
        speakerOptions={speakerOptions}
        canChangeSpeaker={call.canEdit}
        {...transcriptQuery.data}
      />
    );
  }

  // Stop polling if the call is no longer processing
  if (!call.isProcessing) {
    stopPolling();
  }

  return (
    <RecordingLayout
      header={<InterviewDetailsHeader call={call} />}
      video={
        <CallVideo
          mediaPlayerRef={mediaPlayerRef}
          listeners={listeners}
          videoDisplayMode={videoDisplayMode}
          mediaSrc={mediaSrc}
          call={call}
          onSeek={seekAndPlay}
          onCreateClipClick={onCreateClipClick}
          player={player}
          isVideoVisible={isVideoVisible}
          containerRef={containerRef}
          videoRef={videoCallbackRef}
          hideVideo={hideVideo}
          showVideo={focusVideo}
          fullScreenControls={fullScreenControls}
          pipControls={pipControls}
          speakerOptions={speakerOptions}
          notesForCurrentUserOnly={notesForCurrentUserOnly}
        />
      }
      transcript={transcriptSection}
      sidebar={
        <CallSidebar
          call={call}
          onSeek={seekAndPlay}
          onCreateClipClick={onCreateClipClick}
          onCopyTranscript={onCopyTranscript}
          currentTab={currentTab}
          player={player}
          onTabChange={setCurrentTab}
          autoScrollEnabled={autoScrollEnabled}
          setAutoScrollEnabled={setAutoScrollEnabled}
          isVideoCall={videoDisplayMode === VideoDisplayMode.VIDEO}
          isVideoVisible={isVideoVisible}
          hideVideo={hideVideo}
          focusVideo={focusVideo}
          disableAutofill={disableAutofill}
          notesForCurrentUserOnly={notesForCurrentUserOnly}
        />
      }
    >
      {clipRange && mediaSrc && (
        <CreateClipModal
          callId={call.id}
          mediaSrc={mediaSrc}
          initialClipRange={clipRange}
          onClipCreated={() => setCurrentTab(CallTab.CLIPS)}
          onClose={() => setClipRange(null)}
          positionId={call.position?.id}
          videoDisplayMode={videoDisplayMode}
        />
      )}
    </RecordingLayout>
  );
};

export default InterviewDetails;
